import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
// List of supported video platforms - we'll keep this for functionality but focus UI on TikTok
export const SUPPORTED_PLATFORMS = [
  { name: "TikTok", domains: ["tiktok.com", "vm.tiktok.com", "vt.tiktok.com"] },
  { name: "YouTube", domains: ["youtube.com", "youtu.be"] },
  { name: "Vimeo", domains: ["vimeo.com"] },
  { name: "Twitter", domains: ["twitter.com", "x.com"] },
  { name: "Facebook", domains: ["facebook.com", "fb.com", "fb.watch"] },
  { name: "Instagram", domains: ["instagram.com"] },
  { name: "SoundCloud", domains: ["soundcloud.com"] },
  { name: "DailyMotion", domains: ["dailymotion.com"] },
  { name: "Twitch", domains: ["twitch.tv"] }
];
/**
 * Validates if a URL is from a supported video platform
 */
export function isValidVideoUrl(url: string): boolean {
  // Check if empty
  if (!url || !url.trim()) return false;
  try {
    // Try to parse the URL
    const parsedUrl = new URL(url);
    // Check if the domain matches any supported platform
    return SUPPORTED_PLATFORMS.some(platform => 
      platform.domains.some(domain => parsedUrl.hostname.includes(domain))
    );
  } catch (err) {
    // If URL parsing fails, check with regex
    return SUPPORTED_PLATFORMS.some(platform => 
      platform.domains.some(domain => url.includes(domain))
    );
  }
}
/**
 * Legacy function for TikTok URL validation (maintained for backward compatibility)
 */
export function isValidTikTokUrl(url: string): boolean {
  return isValidVideoUrl(url);
}
/**
 * Extracts video ID from a TikTok URL (handles multiple formats)
 */
export function extractVideoId(url: string): string | null {
  try {
    // Check if URL starts with http/https, if not add https://
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    // Try to parse the URL
    const parsedUrl = new URL(fullUrl);
    // TikTok patterns
    if (parsedUrl.hostname.includes("tiktok.com")) {
      // Handle full video URLs: /video/1234567890123456789
      const videoPathMatch = parsedUrl.pathname.match(/\/video\/(\d+)/i);
      if (videoPathMatch && videoPathMatch[1]) {
        return videoPathMatch[1];
      }
      // Handle username/video pattern: /@username/video/1234567890123456789
      const userVideoPathMatch = parsedUrl.pathname.match(/\/@[\w.-]+\/video\/(\d+)/i);
      if (userVideoPathMatch && userVideoPathMatch[1]) {
        return userVideoPathMatch[1];
      }
      // Handle short URLs: /t/ZP8jPGbTk/ or similar patterns
      if (parsedUrl.pathname.includes('/t/')) {
        // Return the path as an identifier since we can't extract the real ID yet
        return parsedUrl.pathname;
      }
      // For vm.tiktok.com URLs, also return the path as an identifier
      if (parsedUrl.hostname === 'vm.tiktok.com') {
        return parsedUrl.pathname;
      }
    }
    // YouTube patterns
    if (parsedUrl.hostname.includes("youtube.com") || parsedUrl.hostname.includes("youtu.be")) {
      // Handle youtube.com/watch?v=VIDEO_ID
      const params = new URLSearchParams(parsedUrl.search);
      const videoId = params.get('v');
      if (videoId) return videoId;
      // Handle youtu.be/VIDEO_ID
      if (parsedUrl.hostname === 'youtu.be') {
        return parsedUrl.pathname.substring(1);
      }
    }
    // For other platforms, just return the pathname as an identifier
    // The backend will handle the extraction
    return parsedUrl.pathname;
  } catch (err) {
    console.error('Error extracting video ID:', err);
  }
  return null;
}
/**
 * Expands a shortened URL to its full form
 * Uses multiple methods to ensure reliable expansion
 */
export async function expandUrl(url: string): Promise<string> {
  console.log('Attempting to expand URL:', url);
  // First, ensure the URL has a protocol
  const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
  // For TikTok URLs, just return the URL with protocol
  // The API will handle the actual processing
  if (urlWithProtocol.includes('tiktok.com')) {
    console.log('TikTok URL detected, using with protocol:', urlWithProtocol);
    return urlWithProtocol;
  }
  try {
    // For other URLs, attempt expansion via fetch
    // Note: This may not work in a browser environment due to CORS
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(urlWithProtocol, {
        method: 'HEAD',
        redirect: 'follow',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (response.url && response.url !== urlWithProtocol) {
        console.log('Successfully expanded URL:', response.url);
        return response.url;
      }
    } catch (err) {
      console.warn('Fetch expansion failed, trying no-cors mode:', err);
      // Try with no-cors as a fallback
      const response = await fetch(urlWithProtocol, {
        method: 'HEAD',
        redirect: 'follow',
        mode: 'no-cors',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    }
    console.log('URL did not expand via fetch. Returning URL with protocol.');
    return urlWithProtocol;
  } catch (err) {
    console.warn('Failed to expand URL:', err);
    // Return the URL with protocol if expansion fails
    return urlWithProtocol;
  }
}
/**
 * Legacy function for TikTok URL expansion (maintained for backward compatibility)
 */
export function expandTikTokUrl(url: string): Promise<string> {
  return expandUrl(url);
}
/**
 * Gets platform name from URL - modified to prioritize TikTok in UI
 */
export function getPlatformNameFromUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    // Check for TikTok first
    const tiktokPlatform = SUPPORTED_PLATFORMS[0];
    if (tiktokPlatform.domains.some(domain => parsedUrl.hostname.includes(domain))) {
      return tiktokPlatform.name;
    }
    // For UI purposes, don't show other platform names
    return "TikTok";
  } catch (err) {
    // If URL parsing fails, try regex matching
    // Check for TikTok first
    const tiktokPlatform = SUPPORTED_PLATFORMS[0];
    if (tiktokPlatform.domains.some(domain => url.includes(domain))) {
      return tiktokPlatform.name;
    }
    // For UI purposes, don't show other platform names
    return "TikTok";
  }
}